import { inject, Injectable } from '@angular/core';
import { NoDeliveryReasonService, SqliteService } from '@app/services';
import { NoDeliveryReasonModel } from '@app/models';

@Injectable({
    providedIn: 'root',
})
export class NoDeliveryReasonRepository
{
    private readonly keys = {
        tableName: 'no_delivery_reasons',
    };
    private sqliteService = inject(SqliteService);
    private noDeliveryReasonService = inject(NoDeliveryReasonService);

    async getNoDeliveryReasons(): Promise<NoDeliveryReasonModel[]>
    {
        const sql = `
            SELECT *
            FROM ${this.keys.tableName};
        `;

        const data = await this.sqliteService.get(sql) ?? [];

        return (data as NoDeliveryReasonModel[]).sort((a, b) =>
        {
            // Handle null sort_order cases
            if (a.sort_order === null && b.sort_order === null)
            {
                return a.name.localeCompare(b.name);
            }

            if (a.sort_order === null)
            {
                return 1;
            }

            if (b.sort_order === null)
            {
                return -1;
            }

            // Both have sort_order values
            if (a.sort_order !== b.sort_order)
            {
                return a.sort_order - b.sort_order;
            }

            // Same sort_order, sort by name
            return a.name.localeCompare(b.name);
        });
    }

    async downloadNoDeliveryReasons(): Promise<NoDeliveryReasonModel[]>
    {
        const noDeliveryReasons = await this.noDeliveryReasonService.getNoDeliveryReasons();

        if (noDeliveryReasons.length > 0)
        {
            await this.saveNoDeliveryReasons(noDeliveryReasons);
        }

        return noDeliveryReasons;
    }

    async saveNoDeliveryReasons(noDeliveryReasons: NoDeliveryReasonModel[]): Promise<void>
    {
        const columns = [
            'id',
            'external_id',
            'name',
            'sort_order',
        ];
        const batchSize = Math.floor(3000 / columns.length);

        for (let i = 0; i < noDeliveryReasons.length; i += batchSize)
        {
            const chunk = noDeliveryReasons.slice(i, i + batchSize);
            const valuePlaceholders = this.sqliteService.getValuePlaceholder(chunk.length, columns.length);
            const sql = `
                INSERT INTO ${this.keys.tableName} (${columns.join(', ')})
                VALUES ${valuePlaceholders} ON CONFLICT(id) DO
                UPDATE
                    SET
                        sort_order = excluded.sort_order,
                    external_id = excluded.external_id,
                    name = excluded.name;
            `;
            const values = chunk.flatMap((r) => [
                r.id,
                r.external_id,
                r.name,
                r.sort_order,
            ]);
            await this.sqliteService.save(sql, values);
        }
    }
}
